import GeneralDialog from '../../../../../shared/general-dialog';
import { useQuery } from '@tanstack/react-query';
import { UserKeys, getCalendarEvents } from '../../../../../api/users-api';
import {
  ApiException,
  Calendar,
  CalendarEventModel,
} from '../../../../../../types';
import FullCalendar from '@fullcalendar/react';
import interactionPlugin from '@fullcalendar/interaction';
import timeGridPlugin from '@fullcalendar/timegrid';
import dayGridPlugin from '@fullcalendar/daygrid';
import { dayjsWithExtensions } from '../../../../../utils/dayjs-config';
import {
  getAllDayDateStringFromEpoch,
  formatAllDayDisplayLabel,
} from '../../../../../utils/calendar-events-utils';
import { useEffect, useState, useRef } from 'react';
import CalendarAddEventDialog from './calendar-add-event-dialog';
import { calendarColors } from '../../../../../utils/calendar-colors';
import { handleErrors } from '../../../../../utils/errors';
import CalendarEventsDeleteDialog from './calendar-events-delete-dialog';
import CalendarEditEventDialog from './calendar-edit-event-dialog';
import AvatarChip from '../../../../../shared/avatar-chip';
import CalendarMoveEventDialog from './calendar-move-event-dialog';
import {
  CalendarRoles,
  isAbleToSeeUIElement,
} from '../../../../../utils/roles/client-roles';
import axios from 'axios';
import { useStore } from '../../../../state/zustand-store';
import UserAvatar from '../../../../../shared/user-avatar';
import { CalendarWrapper } from '../../../../../shared/calendar-wrapper';

import Avatar from '@mui/material/Avatar';
import AvatarGroup from '@mui/material/AvatarGroup';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Menu from '@mui/material/Menu';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import Cancel from '@mui/icons-material/Cancel';
import Check from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import EventRepeat from '@mui/icons-material/EventRepeat';
import LinkOutlined from '@mui/icons-material/LinkOutlined';
import QuestionMark from '@mui/icons-material/QuestionMark';
import SwapHoriz from '@mui/icons-material/SwapHoriz';

interface CalendarEventsDialogProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  selectedCalendar: Calendar;
  changeLogRoles: string[];
  isAllUsers: boolean;
  calendarRoles: string[];
  changeLogMessage?: string;
}

export default function CalendarEventsDialog({
  open,
  handleClose,
  userEmail,
  selectedCalendar,
  changeLogRoles,
  isAllUsers,
  calendarRoles,
  changeLogMessage,
}: CalendarEventsDialogProps) {
  const { setError, setOpenShowable } = useStore();
  const [openCreateEventDialog, setOpenCreateEventDialog] = useState(false);
  const [openDeleteEventDialog, setOpenDeleteEventDialog] = useState(false);
  const [openEditEventDialog, setOpenEditEventDialog] = useState(false);
  const [openMoveEventDialog, setOpenMoveEventDialog] = useState(false);

  const [dateRange, setDateRange] = useState({
    start: dayjsWithExtensions().startOf('month').valueOf(),
    end: dayjsWithExtensions().endOf('month').valueOf(),
  });

  const isInitialLoad = useRef(true);

  const [selectedStartDateTime, setSelectedStartDateTime] = useState<
    number | null
  >(null);
  const [selectedEndDateTime, setSelectedEndDateTime] = useState<number | null>(
    null
  );

  const [anchorPosition, setAnchorPosition] = useState<{
    left: number;
    top: number;
  } | null>(null);

  const [selectedEvent, setSelectedEvent] = useState<CalendarEventModel | null>(
    null
  );

  const handleMenuClose = () => {
    setAnchorPosition(null);
  };

  const { data, isLoading, isFetching, error, isError } = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [
      UserKeys.CalendarEvents,
      selectedCalendar.id,
      userEmail,
      { start: dateRange.start, end: dateRange.end },
      changeLogMessage,
    ],
    queryFn: () =>
      getCalendarEvents(
        {
          id: selectedCalendar.id,
          description: selectedCalendar.description,
          location: selectedCalendar.location,
          summary: selectedCalendar.summary,
          timeZone: selectedCalendar.timeZone,
        },
        dateRange.start,
        dateRange.end,
        changeLogMessage
      ),
    enabled: !isInitialLoad.current, // Don't run query until FullCalendar sets the date range
  });

  useEffect(() => {
    if (isError) {
      if (axios.isAxiosError(error)) {
        const errorResponse = error.response?.data as ApiException | undefined;
        if (errorResponse?.message?.includes('404')) {
          setError({
            header: 'Unable to Pull Events',
            message:
              'This calendar is either not part of your primary or secondary domains, or you no longer have access to see events.',
            exceptionType: 'Default',
          });
          setOpenShowable(true);
        } else {
          handleErrors({
            error,
            snackbarMessage:
              'There was an issue loading events. Please refresh and try again.',
          });
        }
      }
    }
  }, [error, isError, setError, setOpenShowable]);

  const SmallAvatar = styled(Avatar)(() => ({
    width: 12,
    height: 12,
    backgroundColor: 'white',
  }));

  const saidYesGuests =
    selectedEvent?.attendees?.filter(
      (attendee) => attendee.responseStatus === 'accepted'
    ) || [];

  const saidNoGuests =
    selectedEvent?.attendees?.filter(
      (attendee) => attendee.responseStatus === 'declined'
    ) || [];

  const maybeGuests =
    selectedEvent?.attendees?.filter(
      (attendee) => attendee.responseStatus === 'tentative'
    ) || [];

  const selectedUserHasProperPermissions =
    selectedCalendar.accessRole === 'owner' ||
    selectedCalendar.accessRole === 'writer';

  return (
    <GeneralDialog
      open={open}
      handleClose={handleClose}
      maxWidth="lg"
      dialogContent={
        <CalendarWrapper>
          <FullCalendar
            datesSet={(info) => {
              if (
                info.start.valueOf() < dateRange.start ||
                info.end.valueOf() > dateRange.end
              ) {
                setDateRange({
                  start: info.start.valueOf(),
                  end: info.end.valueOf(),
                });
              }
              // Mark that FullCalendar has initialized and set the date range
              if (isInitialLoad.current) {
                isInitialLoad.current = false;
              }
            }}
            height="100%"
            locale="en"
            buttonText={{
              today: 'Today',
              month: 'Month',
              week: 'Week',
              day: 'Day',
            }}
            customButtons={
              selectedUserHasProperPermissions
                ? {
                    addEvent: {
                      text: 'Create Event',
                      click: () => {
                        setSelectedStartDateTime(null);
                        setSelectedEndDateTime(null);
                        setOpenCreateEventDialog(true);
                      },
                    },
                  }
                : undefined
            }
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: selectedUserHasProperPermissions
                ? 'addEvent dayGridMonth,timeGridWeek,timeGridDay'
                : 'dayGridMonth,timeGridWeek,timeGridDay',
            }}
            plugins={[dayGridPlugin, interactionPlugin, timeGridPlugin]}
            initialView="dayGridMonth"
            weekends={true}
            selectable={true}
            select={(info) => {
              if (selectedUserHasProperPermissions) {
                setSelectedStartDateTime(
                  dayjsWithExtensions(info.startStr).valueOf()
                );
                setSelectedEndDateTime(
                  dayjsWithExtensions(info.endStr).valueOf()
                );
                setOpenCreateEventDialog(true);
              }
            }}
            dayMaxEventRows={true}
            eventClick={(info) => {
              const boundingRect = info.el.getBoundingClientRect();
              setAnchorPosition({
                left: boundingRect.left,
                top: boundingRect.bottom,
              });
              const event = data?.find((event) => event.id === info.event.id);
              setSelectedEvent(event || null);
            }}
            events={data?.map((event) => {
              return {
                id: event.id,
                title: event.summary || '(No title)',
                start: event.start.allDay
                  ? getAllDayDateStringFromEpoch(event.start.dateTime)
                  : event.start.dateTime,
                end: event.end.allDay
                  ? getAllDayDateStringFromEpoch(event.end.dateTime)
                  : event.end.dateTime,
                allDay: event.start.allDay,
                color: event.colorId
                  ? calendarColors.event[
                      event.colorId.toString() as keyof typeof calendarColors.event
                    ].background
                  : calendarColors.calendar[
                      selectedCalendar?.colorId.toString() as keyof typeof calendarColors.calendar
                    ].background,
                createdBy: {
                  email: event.creator?.email ?? '',
                  displayName: event.creator?.displayName ?? '',
                },
              };
            })}
          />
          {isLoading || isFetching ? (
            <LinearProgress color="primary" variant="indeterminate" />
          ) : null}
          <Menu
            anchorReference="anchorPosition"
            anchorPosition={anchorPosition || undefined}
            open={Boolean(anchorPosition)}
            onClose={handleMenuClose}
          >
            <Box sx={{ px: 2, py: 0.5, minWidth: 250, maxWidth: 500 }}>
              <Stack flexDirection="row" justifyContent="right" gap={0.5}>
                <Tooltip arrow title="Open in Google Calendar">
                  <IconButton
                    size="small"
                    onClick={() => {
                      window.open(
                        selectedEvent?.htmlLink,
                        '_blank',
                        'noopener noreferrer'
                      );
                    }}
                  >
                    <LinkOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>
                {selectedUserHasProperPermissions ? (
                  <>
                    <Tooltip arrow title="Move">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setOpenMoveEventDialog(true);
                        }}
                        disabled={
                          !isAbleToSeeUIElement(
                            calendarRoles,
                            CalendarRoles.MoveEvents.value
                          )
                        }
                      >
                        <SwapHoriz fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip arrow title="Edit">
                      <IconButton
                        onClick={() => {
                          setOpenEditEventDialog(true);
                        }}
                        disabled={
                          !isAbleToSeeUIElement(
                            calendarRoles,
                            CalendarRoles.UpdateEvents.value
                          )
                        }
                        size="small"
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip arrow title="Delete">
                      <IconButton
                        onClick={() => {
                          setOpenDeleteEventDialog(true);
                        }}
                        disabled={
                          !isAbleToSeeUIElement(
                            calendarRoles,
                            CalendarRoles.RemoveEvents.value
                          )
                        }
                        size="small"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                ) : null}

                <Tooltip arrow title="Close">
                  <IconButton
                    onClick={() => {
                      setAnchorPosition(null);
                    }}
                    size="small"
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
              <Stack flexDirection="row" alignItems="center" gap={1}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    backgroundColor: selectedEvent?.colorId
                      ? calendarColors.event[
                          selectedEvent?.colorId.toString() as keyof typeof calendarColors.event
                        ].background
                      : calendarColors.calendar[
                          selectedCalendar?.colorId.toString() as keyof typeof calendarColors.calendar
                        ].background,
                    borderRadius: 1,
                  }}
                ></Box>
                <Box sx={{ px: 1 }}>
                  <Stack flexDirection="row" alignItems="center" gap={0.5}>
                    <Typography sx={{ fontWeight: 500, fontSize: 20 }}>
                      {selectedEvent?.summary || '(No title)'}
                    </Typography>
                    {selectedEvent?.recurringEventId ? (
                      <Tooltip title="Reccuring Event" arrow>
                        <EventRepeat fontSize="small" color="action" />
                      </Tooltip>
                    ) : null}
                  </Stack>

                  <Typography sx={{ fontWeight: 300, fontSize: 12 }}>
                    {selectedEvent?.start.allDay
                      ? formatAllDayDisplayLabel(selectedEvent?.start.dateTime)
                      : dayjsWithExtensions(
                          selectedEvent?.start.dateTime
                        ).format('dddd, MMMM D')}
                    {selectedEvent?.start.allDay
                      ? ''
                      : ' @ ' +
                        dayjsWithExtensions(
                          selectedEvent?.start.dateTime
                        ).format('h:mma') +
                        ' - ' +
                        dayjsWithExtensions(selectedEvent?.end.dateTime).format(
                          'h:mma'
                        )}
                  </Typography>
                </Box>
              </Stack>
              <Stack
                flexDirection="row"
                alignItems="center"
                sx={{ mt: 1 }}
                gap={0.5}
              >
                {selectedEvent?.attendees?.length ? (
                  <Typography sx={{ fontWeight: 500, fontSize: 12 }}>
                    {selectedEvent?.attendees?.length === 1
                      ? `${selectedEvent?.attendees?.length} guest`
                      : `${selectedEvent?.attendees?.length} guests`}
                  </Typography>
                ) : null}

                {saidNoGuests?.length > 0 ||
                maybeGuests?.length > 0 ||
                saidYesGuests?.length > 0 ? (
                  <Divider orientation="vertical" flexItem />
                ) : null}

                {saidYesGuests?.length > 0 ? (
                  <Typography
                    sx={{ fontWeight: 300, fontSize: 12, color: 'GrayText' }}
                  >
                    {saidYesGuests?.length} accepted
                  </Typography>
                ) : null}
                {saidNoGuests?.length > 0 ? (
                  <Typography
                    sx={{ fontWeight: 300, fontSize: 12, color: 'GrayText' }}
                  >
                    {saidYesGuests?.length > 0 ? ', ' : ''}
                    {saidNoGuests?.length} declined
                  </Typography>
                ) : null}
                {maybeGuests?.length > 0 ? (
                  <Typography
                    sx={{ fontWeight: 300, fontSize: 12, color: 'GrayText' }}
                  >
                    {saidNoGuests?.length > 0 || saidYesGuests?.length > 0
                      ? ', '
                      : ''}
                    {maybeGuests?.length} tentative
                  </Typography>
                ) : null}
              </Stack>

              <AvatarGroup
                max={5}
                sx={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'flex-end',
                  p: 1,
                }}
                total={selectedEvent?.attendees?.length}
              >
                {selectedEvent?.attendees.map((attendee) => {
                  return (
                    <Box key={attendee.email}>
                      <Tooltip arrow title={attendee.email}>
                        <Badge
                          overlap="circular"
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                          }}
                          badgeContent={
                            attendee.responseStatus === 'needsAction' ? null : (
                              <SmallAvatar
                                alt={`${attendee.email}`}
                                sx={{
                                  border: (theme) =>
                                    `1px solid ${
                                      attendee.responseStatus === 'tentative'
                                        ? theme.palette.info.main
                                        : attendee.responseStatus === 'declined'
                                        ? theme.palette.warning.main
                                        : theme.palette.success.main
                                    } !important`,
                                }}
                              >
                                {attendee.responseStatus === 'tentative' ? (
                                  <QuestionMark
                                    color="info"
                                    sx={{ width: 10 }}
                                  />
                                ) : attendee.responseStatus === 'declined' ? (
                                  <Cancel color="warning" sx={{ width: 10 }} />
                                ) : (
                                  <Check color="success" sx={{ width: 10 }} />
                                )}
                              </SmallAvatar>
                            )
                          }
                        >
                          <UserAvatar userEmail={attendee.email} />
                        </Badge>
                      </Tooltip>
                    </Box>
                  );
                })}
              </AvatarGroup>
              <Stack
                flexDirection="row"
                justifyContent="space-between"
                gap={2}
                sx={{ mt: 1 }}
              >
                <Tooltip arrow title={selectedEvent?.organizer?.email || ''}>
                  <span>
                    <AvatarChip
                      email={selectedEvent?.organizer?.email || ''}
                      type="User"
                      label="Organizer"
                    />
                  </span>
                </Tooltip>
                <Tooltip arrow title={selectedEvent?.creator?.email || ''}>
                  <span>
                    <AvatarChip
                      email={selectedEvent?.creator?.email || ''}
                      type="User"
                      label="Creator"
                    />
                  </span>
                </Tooltip>
              </Stack>
            </Box>
          </Menu>
          {openCreateEventDialog && selectedCalendar && (
            <CalendarAddEventDialog
              open={openCreateEventDialog}
              handleClose={() => setOpenCreateEventDialog(false)}
              userEmail={userEmail}
              isAllUsers={isAllUsers}
              changeLogRoles={changeLogRoles}
              selectedCalendar={selectedCalendar}
              selectedStartDateTime={selectedStartDateTime}
              selectedEndDateTime={selectedEndDateTime}
              dateRange={dateRange}
            />
          )}
          {openEditEventDialog && selectedCalendar && selectedEvent && (
            <CalendarEditEventDialog
              open={openEditEventDialog}
              handleClose={() => {
                setOpenEditEventDialog(false);
                setAnchorPosition(null);
              }}
              userEmail={userEmail}
              isAllUsers={isAllUsers}
              changeLogRoles={changeLogRoles}
              selectedCalendar={selectedCalendar}
              selectedEvent={selectedEvent}
            />
          )}
          {openDeleteEventDialog && selectedEvent && (
            <CalendarEventsDeleteDialog
              open={openDeleteEventDialog}
              handleClose={() => {
                setOpenDeleteEventDialog(false);
                setAnchorPosition(null);
              }}
              userEmail={userEmail}
              isAllUsers={isAllUsers}
              changeLogRoles={changeLogRoles}
              selectedEvent={selectedEvent}
              calendarId={selectedCalendar.id}
              dateRange={dateRange}
            />
          )}
          {openMoveEventDialog && selectedEvent && (
            <CalendarMoveEventDialog
              open={openMoveEventDialog}
              handleClose={() => {
                setOpenMoveEventDialog(false);
                setAnchorPosition(null);
              }}
              userEmail={userEmail}
              isAllUsers={isAllUsers}
              changeLogRoles={changeLogRoles}
              selectedEvent={selectedEvent}
              calendarId={selectedCalendar.id}
              dateRange={dateRange}
            />
          )}
        </CalendarWrapper>
      }
    />
  );
}
